'use client'

// React
import React, { useState, useEffect, useRef, useCallback } from 'react'

// Animation
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

// Local
import { ActionButton } from '@/shared/components/cl-chat/action-button'
import { Processing } from '@/shared/components/cl-chat/processing'
import {
  extractTextFromReactNode,
  TextStep,
  CodeStep,
} from '@/shared/components/cl-chat/steps-setup'
import { STEPS, initializeSteps } from '@/shared/components/cl-chat/steps-mdx'

// Utils
import { Join } from '@/lib/utils'

const timeouts = {
  scrollToBottom: 300,
  scrollToBottomFast: 10,
  finishCurrentStep: 50,
  finishCurrentStepFast: 0,
  animateNextCharacter: 5,
  animateNextCharacterSegmentPause: 30,
  animateNextCharacterLinePause: 5,
  animateNextCharacterInitialDelay: 300,
  typeText: 5,
  typeTextInitialDelay: 100,
  typeTextInitialDelayNoProcessing: 200,
  replayButtonDelay: 1000,
  initialDelay: 150,
}

function CLChatInner({
  onPlay,
  onReplay,
  showCover,
}: {
  onPlay: () => void
  onReplay: () => void
  showCover: boolean
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [animationStarted] = useState(true)
  const [renderedElements, setRenderedElements] = useState<React.ReactNode[]>([])

  const processedStepsRef = useRef<Set<number>>(new Set())
  const chatEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
    rootMargin: '0px 0px -100px 0px',
  })

  const scrollToBottom = useCallback(
    (behavior: 'smooth' | 'auto' = 'smooth') => {
      if (!animationStarted || !inView) return

      if (chatContainerRef.current) {
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
              top: chatContainerRef.current.scrollHeight,
              behavior: behavior,
            })
          }
        })
      }
    },
    [animationStarted, inView]
  )

  const finishCurrentStep = useCallback(() => {
    setCurrentStepIndex((prev) => prev + 1)
  }, [])

  initializeSteps(() => finishCurrentStep())

  useEffect(() => {
    setTimeout(() => {
      setCurrentStepIndex(0)
    }, timeouts.initialDelay)
  }, [])

  const appendElement = useCallback(
    (element: React.ReactNode) => {
      setRenderedElements((prev) => [...prev, element])
      setTimeout(() => scrollToBottom(), timeouts.scrollToBottom)
    },
    [scrollToBottom]
  )

  const parseJSXLineToCharacters = useCallback((jsxLine: React.ReactNode) => {
    const rawSegments: Array<{ text: string; className: string }> = []

    if (jsxLine === null || jsxLine === undefined) {
      return []
    }

    const processNode = (node: React.ReactNode, inheritedClassName = '') => {
      if (typeof node === 'string') {
        if (node.length > 0) {
          rawSegments.push({ text: node, className: inheritedClassName })
        }
      } else if (React.isValidElement(node)) {
        const props = node.props as {
          className?: string
          children?: React.ReactNode
        }
        const className = props.className || ''
        const combinedClassName = [inheritedClassName, className].filter(Boolean).join(' ')

        if (node.type === React.Fragment) {
          if (props.children) {
            if (Array.isArray(props.children)) {
              props.children.forEach((child: React.ReactNode) =>
                processNode(child, inheritedClassName)
              )
            } else {
              processNode(props.children, inheritedClassName)
            }
          }
        } else {
          if (props.children) {
            if (Array.isArray(props.children)) {
              props.children.forEach((child: React.ReactNode) =>
                processNode(child, combinedClassName)
              )
            } else {
              processNode(props.children, combinedClassName)
            }
          }
        }
      }
    }

    processNode(jsxLine)

    const mergedSegments: Array<{ text: string; className: string }> = []

    for (const segment of rawSegments) {
      const lastSegment = mergedSegments[mergedSegments.length - 1]

      if (lastSegment && lastSegment.className === segment.className) {
        lastSegment.text += segment.text
      } else {
        mergedSegments.push({ ...segment })
      }
    }

    return Array.isArray(mergedSegments) ? mergedSegments : []
  }, [])

  const parseContentToSegments = useCallback(
    (content: string | React.ReactNode | React.ReactNode[]) => {
      if (typeof content === 'string') {
        return [[{ text: content, className: '' }]]
      } else if (Array.isArray(content)) {
        return content.map((line: React.ReactNode, _index: number) => {
          const segments = parseJSXLineToCharacters(line)
          if (!Array.isArray(segments)) {
            return [{ text: extractTextFromReactNode(line), className: '' }]
          }
          return segments
        })
      } else if (React.isValidElement(content)) {
        const segments = parseJSXLineToCharacters(content)
        if (!Array.isArray(segments)) {
          return [[{ text: extractTextFromReactNode(content), className: '' }]]
        }
        return [segments]
      } else {
        const textContent = extractTextFromReactNode(content)
        return [[{ text: textContent, className: '' }]]
      }
    },
    [parseJSXLineToCharacters]
  )

  const startSystemTextTypewriter = useCallback(
    (step: TextStep, withProcessing: boolean = false) => {
      const allLines = parseContentToSegments(step.content)

      let currentLineIndex = 0
      let currentSegmentIndex = 0
      let currentCharIndex = 0
      const displayedLines: Array<Array<{ text: string; className: string }>> = []

      const createSystemElement = (
        lines: Array<Array<{ text: string; className: string }>>,
        showCursor: boolean
      ) => (
        <motion.div
          className="flex justify-start"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className={Join('text-sm text-gray-200/90', step.classNames)}>
            <div className="mb-1">
              {lines.map((lineSegments, lineIndex) => {
                if (!Array.isArray(lineSegments)) {
                  return null
                }

                return (
                  <div key={lineIndex} className="w-full">
                    {(lineSegments || []).map((segment, segmentIndex) => (
                      <span key={segmentIndex} className={segment.className}>
                        {segment.text}
                      </span>
                    ))}
                    {showCursor && lineIndex === lines.length - 1 && (
                      <span className="animate-pulse">|</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </motion.div>
      )

      appendElement(createSystemElement([], true))

      const animateNextCharacter = () => {
        if (currentLineIndex < allLines.length) {
          const currentLine = allLines[currentLineIndex]

          if (currentSegmentIndex < currentLine.length) {
            const currentSegment = currentLine[currentSegmentIndex]

            if (currentCharIndex < currentSegment.text.length) {
              if (!displayedLines[currentLineIndex]) {
                displayedLines[currentLineIndex] = []
              }
              if (!displayedLines[currentLineIndex][currentSegmentIndex]) {
                displayedLines[currentLineIndex][currentSegmentIndex] = {
                  text: '',
                  className: currentSegment.className,
                }
              }

              displayedLines[currentLineIndex][currentSegmentIndex].text =
                currentSegment.text.slice(0, currentCharIndex + 1)

              setRenderedElements((prev) => {
                const newElements = [...prev]
                newElements[newElements.length - 1] = createSystemElement([...displayedLines], true)
                return newElements
              })

              currentCharIndex++
              setTimeout(animateNextCharacter, timeouts.typeText)
            } else {
              currentSegmentIndex++
              currentCharIndex = 0
              setTimeout(animateNextCharacter, timeouts.typeText)
            }
          } else {
            currentLineIndex++
            currentSegmentIndex = 0
            currentCharIndex = 0
            setTimeout(() => scrollToBottom(), timeouts.scrollToBottomFast)
            setTimeout(animateNextCharacter, timeouts.typeText)
          }
        } else {
          setRenderedElements((prev) => {
            const newElements = [...prev]
            newElements[newElements.length - 1] = createSystemElement([...displayedLines], false)
            return newElements
          })
          setTimeout(() => scrollToBottom(), timeouts.scrollToBottomFast)
          setTimeout(() => finishCurrentStep(), timeouts.finishCurrentStep)
        }
      }

      const initialDelay = withProcessing
        ? timeouts.typeTextInitialDelay
        : timeouts.typeTextInitialDelayNoProcessing

      setTimeout(animateNextCharacter, initialDelay)
    },
    [finishCurrentStep, scrollToBottom, appendElement, parseContentToSegments]
  )

  const startCodeTypewriter = useCallback(
    (step: CodeStep) => {
      const allLines = parseContentToSegments(step.content)

      let currentLineIndex = 0
      let currentSegmentIndex = 0
      let currentCharIndex = 0
      const displayedLines: Array<Array<{ text: string; className: string }>> = []

      const createCodeElement = (
        lines: Array<Array<{ text: string; className: string }>>,
        showCursor: boolean
      ) => (
        <motion.div
          className="mb-6 rounded-lg bg-[#1c1a23]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-3">
            <div className="font-mono text-xs text-white">
              {lines.map((lineSegments, lineIndex) => {
                if (!Array.isArray(lineSegments)) {
                  return null
                }
                const lineNumber = lineIndex + 1
                const isHighlighted = step.highlightedLines?.includes(lineNumber) || false
                const highlightColor = step.highlightColor || null

                const getHighlightClass = () => {
                  if (isHighlighted && highlightColor) {
                    switch (highlightColor) {
                      case 'red':
                        return 'bg-[#361E24]'
                      case 'blue':
                        return 'bg-blue-400/20'
                      case 'green':
                        return 'bg-[#129850]/20'
                      default:
                        return ''
                    }
                  }
                  return ''
                }

                return (
                  <div key={lineIndex} className={`w-full py-0.5 pl-2 ${getHighlightClass()}`}>
                    <code className="font-mono text-xs">
                      {(lineSegments || []).map((segment, segmentIndex) => (
                        <span key={segmentIndex} className={segment.className}>
                          {segment.text}
                        </span>
                      ))}
                      {showCursor && lineIndex === lines.length - 1 && (
                        <span className="animate-pulse">|</span>
                      )}
                    </code>
                  </div>
                )
              })}
            </div>
          </div>
        </motion.div>
      )

      appendElement(createCodeElement([], true))

      const animateNextCharacter = () => {
        if (currentLineIndex < allLines.length) {
          const currentLine = allLines[currentLineIndex]

          if (currentSegmentIndex < currentLine.length) {
            const currentSegment = currentLine[currentSegmentIndex]

            if (currentCharIndex < currentSegment.text.length) {
              if (!displayedLines[currentLineIndex]) {
                displayedLines[currentLineIndex] = []
              }
              if (!displayedLines[currentLineIndex][currentSegmentIndex]) {
                displayedLines[currentLineIndex][currentSegmentIndex] = {
                  text: '',
                  className: currentSegment.className,
                }
              }

              displayedLines[currentLineIndex][currentSegmentIndex].text =
                currentSegment.text.slice(0, currentCharIndex + 1)

              setRenderedElements((prev) => {
                const newElements = [...prev]
                newElements[newElements.length - 1] = createCodeElement([...displayedLines], true)
                return newElements
              })

              currentCharIndex++
              setTimeout(animateNextCharacter, timeouts.animateNextCharacter)
            } else {
              currentSegmentIndex++
              currentCharIndex = 0
              setTimeout(animateNextCharacter, timeouts.animateNextCharacterSegmentPause)
            }
          } else {
            currentLineIndex++
            currentSegmentIndex = 0
            currentCharIndex = 0
            setTimeout(() => scrollToBottom(), timeouts.scrollToBottomFast)
            setTimeout(animateNextCharacter, timeouts.animateNextCharacterLinePause)
          }
        } else {
          setRenderedElements((prev) => {
            const newElements = [...prev]
            newElements[newElements.length - 1] = createCodeElement([...displayedLines], false)
            return newElements
          })
          setTimeout(() => scrollToBottom(), timeouts.scrollToBottomFast)
          setTimeout(() => finishCurrentStep(), timeouts.finishCurrentStep)
        }
      }

      setTimeout(animateNextCharacter, timeouts.animateNextCharacterInitialDelay)
    },
    [finishCurrentStep, scrollToBottom, appendElement, parseContentToSegments]
  )

  useEffect(() => {
    if (!animationStarted || currentStepIndex >= STEPS.length) return

    if (processedStepsRef.current.has(currentStepIndex)) {
      return
    }
    processedStepsRef.current.add(currentStepIndex)

    const step = STEPS[currentStepIndex]

    switch (step.type) {
      case 'userText':
        const userElement = (
          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            onAnimationComplete={() => {
              setTimeout(() => finishCurrentStep(), timeouts.finishCurrentStep)
            }}
          >
            <div className="relative mb-3 max-w-[80%] min-w-14 rounded-l-xl bg-slate-500/50 px-4 py-2 text-white">
              <p className="relative text-sm">{step.content}</p>
            </div>
          </motion.div>
        )
        appendElement(userElement)
        break

      case 'systemText':
        if (step.showProcessing) {
          const processingElement = (
            <Processing
              mode={step.showProcessing.mode}
              classNames={step.classNames}
              elementId={`processing-${currentStepIndex}`}
            />
          )
          appendElement(processingElement)

          setTimeout(() => {
            setRenderedElements((prev) => prev.slice(0, -1))
            startSystemTextTypewriter(step, true)
          }, step.showProcessing.duration)
        } else {
          startSystemTextTypewriter(step, false)
        }
        break

      case 'code':
        if (step.showProcessing) {
          const processingElement = (
            <Processing
              mode={step.showProcessing.mode}
              elementId={`processing-${currentStepIndex}`}
            />
          )
          appendElement(processingElement)

          setTimeout(() => {
            setRenderedElements((prev) => prev.slice(0, -1))
            startCodeTypewriter(step)
          }, step.showProcessing.duration)
        } else {
          startCodeTypewriter(step)
        }
        break

      case 'restart':
        const replayElement = (
          <motion.div
            className="mt-8 mb-4 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <ActionButton onClick={onReplay} className="bg-purple-600/80 hover:bg-purple-700">
              Replay
            </ActionButton>
          </motion.div>
        )
        setTimeout(() => {
          appendElement(replayElement)
        }, timeouts.replayButtonDelay)
        break
    }
  }, [
    animationStarted,
    currentStepIndex,
    finishCurrentStep,
    appendElement,
    startCodeTypewriter,
    startSystemTextTypewriter,
    onReplay,
  ])

  const handlePlayClick = () => {
    onPlay()
  }

  return (
    <div
      ref={ref}
      className="relative h-full w-full rounded-2xl border border-white/10 bg-linear-to-b from-[#1C1A26] to-[#211F2A] font-mono"
    >
      <div className="flex h-full w-full flex-col">
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-6"
          style={{ scrollBehavior: 'smooth' }}
        >
          <div>
            {renderedElements.map((element, index) => (
              <div key={index}>{element}</div>
            ))}
            <div ref={chatEndRef} className="h-1" />
          </div>
        </div>
      </div>

      {showCover && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-sm">
          <div className="space-y-6 px-8 text-center">
            <h2 className="mx-auto max-w-[28ch] text-2xl leading-tight font-medium text-white md:text-3xl">
              See how to use custom React components in MDX
            </h2>
            <ActionButton
              title="Play the animation"
              ariaLabel="Play the animation"
              onClick={handlePlayClick}
              className="transform rounded-full bg-green-600 px-8 py-4 text-lg font-medium text-white shadow-lg transition-colors hover:scale-105 hover:bg-green-700 hover:shadow-xl"
            >
              Play
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  )
}

export default function CLChat() {
  const [componentKey, setComponentKey] = useState(0)
  const [showCover, setShowCover] = useState(true)

  const handlePlay = () => {
    setShowCover(false)
    setComponentKey((prev) => prev + 1)
  }

  const handleReplay = () => {
    setComponentKey((prev) => prev + 1)
  }

  return (
    <CLChatInner
      key={componentKey}
      onPlay={handlePlay}
      onReplay={handleReplay}
      showCover={showCover}
    />
  )
}
